/**
 * SpecialEditorFieldRows — the shared labeled-row primitives for CC-3
 * special-editor forms (REVCLOUD-PROPS-EDITOR / HATCH-EDIT-DIALOG …).
 *
 * Every special-editor form lays its fields out as the same `dimstyle-editor-row`
 * (label + control) markup the dialog suite uses. That row is ONE UI truth —
 * extracted here so each form supplies only its field data (id / testid /
 * translated label / value / onChange) and never re-copies the markup (dup gate:
 * the forms previously carried token-identical `<div className="dimstyle-editor-
 * row">…</div>` blocks). The controls stay CONTROLLED (value + onChange lifted to
 * the form's `useSpecialEditorFieldDraft` draft); these components own no state.
 */

import type { ReactElement } from "react";

import { useT } from "../i18n";
import type { MessageKey } from "../i18n";

/** One `<select>` option — the raw value plus the i18n key for its label. The row
 *  translates it via `useT`, so each form passes its `{ value, labelKey }` table
 *  verbatim (no per-form translate helper to duplicate). */
export interface SpecialEditorSelectOption {
  value: string;
  labelKey: MessageKey;
}

/** A labeled `<select>` row (enum / boolean fields). */
export function SpecialEditorSelectRow({
  id,
  testId,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  testId: string;
  label: string;
  value: string;
  options: ReadonlyArray<SpecialEditorSelectOption>;
  onChange: (value: string) => void;
}): ReactElement {
  const t = useT();
  return (
    <div className="dimstyle-editor-row">
      <label className="dimstyle-editor-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="dimstyle-editor-select"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {t(o.labelKey)}
          </option>
        ))}
      </select>
    </div>
  );
}

/** A labeled numeric `<input>` row (`step="any"`; optional `min`). */
export function SpecialEditorNumberRow({
  id,
  testId,
  label,
  value,
  onChange,
  min,
}: {
  id: string;
  testId: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
}): ReactElement {
  return (
    <div className="dimstyle-editor-row">
      <label className="dimstyle-editor-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="dimstyle-editor-input"
        data-testid={testId}
        type="number"
        step="any"
        {...(min !== undefined ? { min } : {})}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** A labeled free-text `<input>` row (e.g. the Hatch pattern name). */
export function SpecialEditorTextRow({
  id,
  testId,
  label,
  value,
  onChange,
}: {
  id: string;
  testId: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <div className="dimstyle-editor-row">
      <label className="dimstyle-editor-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="dimstyle-editor-input"
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
